import { createContext, useContext, type ReactNode } from "react";
import type { PinRef } from "~/services/pinnedArtifacts";

// Conversation-scoped values that deep descendants need (e.g. artifact
// renderers inside markdown) but don't want to receive via prop drilling.
//
// At most one of `conversationId`, `shareToken` or `pin` is populated:
//   - Owner views set `conversationId` (UUID); artifact fetches use the
//     owner-only endpoint.
//   - Admin reviewer views set `conversationId` and `isAdmin`; artifact
//     fetches use the admin endpoint, since the owner one refuses a
//     cross-user read.
//   - Share-token views set `shareToken`; artifact fetches use the
//     token-scoped previews endpoint, with the shares-table row acting as
//     the authorization.
//   - A project's shared-artifact surfaces set `pin`; downloads go through
//     the pin's routes, which any project member may call, since the
//     session holding the artifact is usually another member's.
interface ConversationContextValue {
  conversationId: string | null;
  shareToken: string | null;
  isAdmin: boolean;
  pin: PinRef | null;
}

const ConversationContext = createContext<ConversationContextValue | null>(null);

export const ConversationProvider = ({
  conversationId,
  shareToken = null,
  isAdmin = false,
  pin = null,
  children,
}: {
  conversationId: string | null;
  shareToken?: string | null;
  isAdmin?: boolean;
  pin?: PinRef | null;
  children: ReactNode;
}) => (
  <ConversationContext.Provider value={{ conversationId, shareToken, isAdmin, pin }}>
    {children}
  </ConversationContext.Provider>
);

export const useConversationContext = (): ConversationContextValue => {
  const ctx = useContext(ConversationContext);
  if (!ctx) {
    throw new Error("useConversationContext must be used within <ConversationProvider>");
  }
  return ctx;
};
